import type { AggregatedRiskWidgetRuntimeState } from '.';
import { createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import type { AggregatedRiskTableFilters, AggregatedRiskTableKind } from 'src/queries/aggregatedRiskTables';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { FullKeyType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';

interface AggregatedRiskWidgetTableModelInit {
    columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    columnTemplateError: ReadonlySignal<Error | null>;
    config: AggregatedRiskWidgetConfig;
    devtoolsName: string;
    filters: ReadonlySignal<AggregatedRiskTableFilters>;
    groupByIndicatorId: ReadonlySignal<string | null>;
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    table: TableModel;
    tableKind: AggregatedRiskTableKind;
    tableQueryOptionsFactory: (args: {
        columnTemplate: ColumnTemplate;
        config: AggregatedRiskWidgetConfig;
        filters: AggregatedRiskTableFilters;
        slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
        userViewKey: string;
    }) => QueryObserverOptions<FullKeyType, unknown, FullKeyType>;
    widgetId: WidgetId;
}

function makeInitializingTableQueryOptions(tableKind: AggregatedRiskTableKind, widgetId: WidgetId): TableCreationQuery {
    return {
        queryKey: ['aggregated-risk-widget-table', tableKind, widgetId, 'initializing'],
        queryFn: async () => null,
        enabled: false,
        staleTime: 0,
        gcTime: 0,
    };
}

export const AggregatedRiskWidgetTableModel = createModel((init: AggregatedRiskWidgetTableModelInit) => {
    const disposableStack = new DisposableStack();

    const stateSignal = signal<AggregatedRiskWidgetRuntimeState>({ type: 'initializing' });
    const state: ReadonlySignal<AggregatedRiskWidgetRuntimeState> = stateSignal;
    const tableQueryOptions = signal<TableCreationQuery>(
        makeInitializingTableQueryOptions(init.tableKind, init.widgetId),
    );

    init.table.init({
        queryOptions: tableQueryOptions,
        initBounds: { firstRow: 0, windowSize: 100 },
        devtoolsName: init.devtoolsName,
    });

    disposableStack.defer(
        effect(() => {
            const columnTemplateError = init.columnTemplateError.value;
            if (columnTemplateError) {
                stateSignal.value = { type: 'error', error: columnTemplateError };
                return;
            }

            if (init.table.failureCount.value > 0 && init.table.error) {
                if (stateSignal.value.type !== 'error' || stateSignal.value.error !== init.table.error) {
                    stateSignal.value = { type: 'error', error: init.table.error };
                }
                return;
            }

            if (!init.columnTemplate.value || !init.groupByIndicatorId.value) {
                if (stateSignal.value.type !== 'initializing') {
                    stateSignal.value = { type: 'initializing' };
                }
                return;
            }

            if (init.table.connectionStatus.value === 'connected' && stateSignal.value.type !== 'ready') {
                stateSignal.value = { type: 'ready' };
            }
        }),
    );

    disposableStack.defer(
        effect(() => {
            const columnTemplate = init.columnTemplate.value;

            if (!columnTemplate || !init.groupByIndicatorId.value) {
                tableQueryOptions.value = makeInitializingTableQueryOptions(init.tableKind, init.widgetId);
                return;
            }

            tableQueryOptions.value = init.tableQueryOptionsFactory({
                columnTemplate,
                config: init.config,
                filters: init.filters.value,
                slugMapServiceProvider: init.slugMapServiceProvider,
                userViewKey: init.widgetId,
            });
        }),
    );

    return {
        state,
        [Symbol.dispose](): void {
            disposableStack.dispose();
        },
    };
});

export type AggregatedRiskWidgetTableModel = InstanceType<typeof AggregatedRiskWidgetTableModel>;
